import { useEffect, useState, useCallback } from 'react'
import DocumentUpload from './components/DocumentUpload'
import ConfirmCompleteModal from './modals/ConfirmCompleteModal'
import DocumentTabs from './components/DocumentTabs'
import TranscriptRequirementUploads from './components/TranscriptRequirementUploads'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

export default function DocumentCenter() {
  const { applicationId } = useParams()
  const [searchParams] = useSearchParams()
  const quarter = searchParams.get('quarter') ?? ''
  const type = searchParams.get('type') ?? ''
  const navigate = useNavigate()

  const [selectedType, setSelectedType] = useState('')
  const [showConfirmCompleteModal, setShowConfirmCompleteModal] = useState(false)
  const [nothingToUpload, setNothingToUpload] = useState([])
  const [allDocumentsUploaded, setAllDocumentsUploaded] = useState(false)
  const [dcLoading, setDcLoading] = useState(true)
  const [transcriptRequirements, setTranscriptRequirements] = useState([])
  const [legacyTabs, setLegacyTabs] = useState([])

  const handleCheckDocuments = useCallback(async () => {
    const checkResponse = await fetch(`${apiURL}/api/applications/${applicationId}/documents/check/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': account.accessToken
      },
      body: JSON.stringify({
        nothingToUpload,
        type,
        quarter
      })
    })
    const checkData = await checkResponse.json()
    if (checkData.response.error) {
      if (checkData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleCheckDocuments() : navigate('/', { state: { expired: true } })
      } else
        console.error(checkData.response.error)
    } else
      setAllDocumentsUploaded(checkData.response.allUploaded)
  }, [applicationId, nothingToUpload, type, quarter, navigate])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setDcLoading(true)
      try {
        const res = await fetch(
          `${apiURL}/api/applications/${applicationId}/document-center?type=${encodeURIComponent(type)}&quarter=${encodeURIComponent(quarter)}`,
          { headers: { Authorization: account.accessToken } }
        )
        const data = await res.json()
        if (cancelled) return
        if (data.response?.error === 'Failed To Authenticate Token.') {
          const refreshed = await refreshAccessToken()
          if (refreshed && refreshed.length > 0) return load()
          navigate('/', { state: { expired: true } })
          return
        }
        const tr = data.response?.transcriptDocumentRequirements
        setTranscriptRequirements(Array.isArray(tr) ? tr : [])
        setLegacyTabs(Array.isArray(data.response?.documentCenterTabs) ? data.response.documentCenterTabs : [])
      } catch {
        if (!cancelled) {
          setTranscriptRequirements([])
          setLegacyTabs([])
        }
      } finally {
        if (!cancelled) setDcLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [applicationId, type, quarter, navigate])

  useEffect(() => {
    if (!dcLoading)
      handleCheckDocuments()
  }, [nothingToUpload, dcLoading, handleCheckDocuments])

  const transcriptMode = transcriptRequirements.length > 0
  const isQuarterScopedUpload = Boolean(quarter && type)
  const scopedTab = legacyTabs.find(tab => tab.key === type)

  useEffect(() => {
    if (!isQuarterScopedUpload || !type || selectedType) return
    setSelectedType(type)
  }, [isQuarterScopedUpload, type, selectedType])

  if (dcLoading) {
    return (
      <div className='w-full flex flex-col items-center justify-center mt-24 gap-3'>
        <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary h-10 w-10 animate-spin' />
        <p className='text-slate-600 dark:text-slate-300'>Loading document center…</p>
      </div>
    )
  }

  if (transcriptMode) {
    return (
      <div className='w-full flex flex-col mt-8 pb-12 px-2 sm:px-4 transition-colors duration-300'>
        <h1 className='text-3xl font-bold text-center text-slate-900 dark:text-white mb-4'>Document Center</h1>
        <p className='text-center text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl mx-auto text-sm sm:text-base mb-2 px-2'>
          Your processor has listed the documents needed by quarter—use each quarter tab, then upload the files for that period. Required items must be uploaded for every quarter before you can finish this step.
        </p>
        <div className='w-11/12 mx-auto'>
          <TranscriptRequirementUploads
            applicationId={applicationId}
            requirements={transcriptRequirements}
            onChanged={handleCheckDocuments}
          />
        </div>
        <div className='flex flex-col items-center gap-4 w-11/12 mx-auto mt-12'>
          <button
            type='button'
            onClick={() => setShowConfirmCompleteModal(true)}
            disabled={!allDocumentsUploaded}
            className={`px-8 py-4 rounded-xl shadow-md text-white font-bold transition-all ${allDocumentsUploaded ? 'bg-ertc-primary hover:bg-ertc-primary/90 active:scale-95' : 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed shadow-none'}`}
          >
            I have completed my document upload
          </button>
          <p className='text-slate-500 dark:text-slate-400 italic text-sm text-center max-w-2xl'>
            Upload all required documents in every quarter tab before clicking the button.
          </p>
        </div>
        {showConfirmCompleteModal && (
          <ConfirmCompleteModal applicationId={applicationId} setShowConfirmCompleteModal={setShowConfirmCompleteModal} />
        )}
      </div>
    )
  }

  return (
    <div className='w-full flex flex-col items-center mt-8 p-4 sm:p-8 xl:p-12 transition-colors duration-300'>
      <h1 className='text-3xl font-bold text-center text-slate-900 dark:text-white mb-4'>Document Center</h1>
      <p className='text-center text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl text-sm sm:text-base'>
        {isQuarterScopedUpload ? (
          <>
            Please upload the required {scopedTab?.title ?? 'documents'} for this quarter
            {scopedTab?.description ? (
              <> — {scopedTab.description.replace(/\.$/, '')}</>
            ) : null}
            . Once you have finished, click the
            <span className='font-semibold text-slate-800 dark:text-gray-100'> &apos;I have completed my document upload&apos; </span> button at the bottom of this page.
          </>
        ) : (
          <>
            Please go through each tab below, and upload the corresponding documents for that tab.
            <br />
            If you have no documents for that tab, select the
            <span className='font-semibold text-slate-800 dark:text-gray-100'> &apos;I have nothing to upload for this category&apos; </span> checkbox.
            <br />
            Once you have uploaded all documents, please click the
            <span className='font-semibold text-slate-800 dark:text-gray-100'> &apos;I have completed my document upload&apos; </span> button at the bottom of this page.
          </>
        )}
      </p>
      <DocumentTabs
        applicationId={applicationId}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        prefetchedTabs={legacyTabs}
      />
      {selectedType ? (
        <>
          <DocumentUpload
            applicationId={applicationId}
            selectedType={selectedType}
            nothingToUpload={nothingToUpload}
            setNothingToUpload={setNothingToUpload}
            handleCheckDocuments={handleCheckDocuments}
            isQuarterScopedUpload={isQuarterScopedUpload}
          />
          <div className='flex flex-col items-center gap-4 w-full mt-12'>
            <button
              type='button'
              onClick={() => setShowConfirmCompleteModal(true)}
              disabled={!allDocumentsUploaded}
              className={`px-8 py-4 rounded-xl shadow-md text-white font-bold transition-all ${allDocumentsUploaded ? 'bg-ertc-primary hover:bg-ertc-primary/90 active:scale-95' : 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed shadow-none'}`}
            >
              I have completed my document upload
            </button>

            <p className='text-slate-500 dark:text-slate-400 italic text-sm text-center max-w-2xl'>
              {isQuarterScopedUpload ? (
                <>
                  NOTE: Documents save automatically as you upload. Click the button above only after you have{' '}
                  <span className='font-bold text-slate-700 dark:text-slate-300'>FINISHED</span> uploading for this quarter.
                </>
              ) : (
                <>
                  NOTE: As you upload documents, they are automatically saving. Please only click the button above once you have{' '}
                  <span className='font-bold text-slate-700 dark:text-slate-300'>FINISHED</span> uploading all documents.
                </>
              )}
            </p>
          </div>
          {showConfirmCompleteModal && (
            <ConfirmCompleteModal applicationId={applicationId} setShowConfirmCompleteModal={setShowConfirmCompleteModal} />
          )}
        </>
      ) : (
        <p className='mt-12 text-slate-500 dark:text-slate-400 italic text-lg'>Select a tab to start uploading</p>
      )}
    </div>
  )
}
